<template>
  <AdminLayout>
    <div class="d-flex flex-column">
      <div class="d-flex flex-row align-center mb-3">
        <h1 class="outfit-regular">Cocoro</h1>
        <v-spacer></v-spacer>
      </div>
      <v-progress-linear :value="progressValue" stream color="yellow darken-3" class="mt-2 mb-2"></v-progress-linear>

      <v-tabs v-model="tab" background-color="transparent">
        <v-tabs-slider color="black"></v-tabs-slider>
        <v-tab>Chats</v-tab>
        <v-tab>Agents</v-tab>
        <v-tab>Templates</v-tab>
      </v-tabs>
      <v-tabs-items v-model="tab">

        <!-- Chats Tab -->
        <v-tab-item>
          <v-sheet class="pa-6" rounded color="grey lighten-4">
            <div class="d-flex flex-row" style="min-height: 600px;">
              <!-- Left: Agent & Chat Selection -->
              <div class="d-flex flex-column mr-4" style="width: 280px; min-width: 280px;">
                <!-- Agent Selector -->
                <div class="subtitle-2 grey--text mb-2">Select Agent</div>
                <v-select
                  v-model="chatAgentId"
                  :items="agents"
                  item-text="name"
                  item-value="id"
                  solo
                  outlined
                  dense
                  hide-details
                  placeholder="Choose an agent..."
                  class="mb-4"
                >
                  <template v-slot:item="{ item }">
                    <div class="d-flex flex-column py-1">
                      <div>{{ item.name }}</div>
                      <div class="caption grey--text">{{ getTemplateName(item.templateId) || 'No template' }}</div>
                    </div>
                  </template>
                </v-select>

                <!-- New Chat Button -->
                <v-btn 
                  color="black" 
                  dark 
                  class="elevation-0 mb-4" 
                  :disabled="!chatAgentId"
                  @click="startNewChat"
                >
                  <v-icon left>mdi-plus</v-icon> New Chat
                </v-btn>

                <!-- Chat History -->
                <div class="subtitle-2 grey--text mb-2">Chat History</div>
                <v-card outlined class="flex-grow-1" style="overflow-y: auto;">
                  <v-list dense v-if="chats.length > 0">
                    <v-list-item-group v-model="selectedChatIndex" color="black">
                      <v-list-item 
                        v-for="(c, i) in chats" 
                        :key="c.id"
                        @click="selectChat(i)"
                      >
                        <v-list-item-content>
                          <v-list-item-title class="caption">{{ getChatAgentName(c.agentId) }}</v-list-item-title>
                          <v-list-item-subtitle class="caption grey--text">
                            {{ c.messages.length }} messages · {{ formatDate(c.createdAt) }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-chip x-small :color="c.complete ? 'green' : 'orange'" dark>
                            {{ c.complete ? 'Complete' : 'In Progress' }}
                          </v-chip>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list-item-group>
                  </v-list>
                  <div v-else class="pa-4 text-center grey--text caption">
                    No chats yet
                  </div>
                </v-card>
              </div>

              <!-- Middle: Chat Window -->
              <div class="d-flex flex-column flex-grow-1 mr-4">
                <template v-if="selectedChat">
                  <!-- Chat Header -->
                  <div class="d-flex flex-row align-center mb-3">
                    <div class="d-flex flex-column">
                      <div class="subtitle-1 font-weight-medium">{{ getChatAgentName(selectedChat.agentId) }}</div>
                      <div class="caption grey--text">{{ getChatTemplateName(selectedChat.agentId) }}</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-btn icon small @click="clearChat" title="Clear chat">
                      <v-icon>mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>

                  <!-- Messages -->
                  <v-card outlined class="flex-grow-1 mb-3 pa-3" style="overflow-y: auto; max-height: 420px;">
                    <div v-if="selectedChat.messages.length === 0" class="d-flex flex-column align-center justify-center pa-6 grey--text" style="height: 100%;">
                      <v-icon large color="grey lighten-1" class="mb-2">mdi-chat-outline</v-icon>
                      <div class="caption">No messages yet. Start the conversation!</div>
                    </div>
                    <div v-else class="d-flex flex-column">
                      <div 
                        v-for="(msg, i) in selectedChat.messages" 
                        :key="i"
                        class="d-flex flex-row mb-3"
                        :class="msg.role === 'user' ? 'justify-end' : 'justify-start'"
                      >
                        <v-card 
                          :color="msg.role === 'user' ? 'black' : 'white'" 
                          :dark="msg.role === 'user'"
                          class="pa-3"
                          style="max-width: 80%;"
                          :outlined="msg.role !== 'user'"
                        >
                          <div class="caption font-weight-medium mb-1" :class="msg.role === 'user' ? '' : 'grey--text'">
                            {{ msg.role === 'user' ? 'You' : getChatAgentName(selectedChat.agentId) }}
                          </div>
                          <div style="white-space: pre-wrap;">{{ msg.content }}</div>
                        </v-card>
                      </div>
                    </div>
                  </v-card>

                  <!-- Input -->
                  <div class="d-flex flex-row align-center">
                    <v-text-field
                      v-model="chatInput"
                      solo
                      outlined
                      dense
                      hide-details
                      placeholder="Type your message..."
                      @keyup.enter="sendMessage"
                      class="mr-2"
                    ></v-text-field>
                    <v-btn 
                      color="black" 
                      dark 
                      class="elevation-0"
                      :loading="chatLoading"
                      @click="sendMessage"
                    >
                      <v-icon>mdi-send</v-icon>
                    </v-btn>
                  </div>
                </template>

                <!-- No Chat Selected -->
                <div v-else class="d-flex flex-column align-center justify-center pa-12 grey--text" style="height: 100%;">
                  <v-icon x-large color="grey lighten-1" class="mb-3">mdi-chat-processing-outline</v-icon>
                  <div class="subtitle-1">No chat selected</div>
                  <div class="caption">Select an agent and start a new chat</div>
                </div>
              </div>

              <!-- Right: Extraction Panel -->
              <div class="d-flex flex-column" style="width: 300px; min-width: 300px;">
                <div class="subtitle-2 grey--text mb-2">Cocoro Evaluation</div>
                
                <template v-if="selectedChat">
                  <!-- Extraction Status -->
                  <v-card outlined class="pa-3 mb-3">
                    <div class="d-flex flex-row align-center mb-2">
                      <div class="subtitle-2">Status</div>
                      <v-spacer></v-spacer>
                      <v-chip small :color="selectedChat.complete ? 'green' : 'orange'" dark>
                        {{ selectedChat.complete ? 'Complete' : 'In Progress' }}
                      </v-chip>
                    </div>
                    <v-progress-linear
                      :value="extractionProgress"
                      color="green"
                      height="8"
                      rounded
                    ></v-progress-linear>
                    <div class="caption grey--text mt-1">
                      {{ extractedFieldCount }}/{{ totalFieldCount }} fields extracted
                    </div>
                  </v-card>

                  <!-- Extracted Fields -->
                  <v-card outlined class="pa-3 mb-3 flex-grow-1" style="overflow-y: auto;">
                    <div class="subtitle-2 mb-2">Extracted Fields</div>
                    <div v-if="chatTemplateFields.length === 0" class="caption grey--text">
                      No template linked to this agent
                    </div>
                    <div v-else>
                      <div 
                        v-for="field in chatTemplateFields" 
                        :key="field.key"
                        class="d-flex flex-row align-center py-2"
                        style="border-bottom: 1px solid #eee;"
                      >
                        <div class="d-flex flex-column flex-grow-1">
                          <div class="caption font-weight-medium">
                            {{ field.label }}
                            <span v-if="field.required" class="red--text">*</span>
                          </div>
                          <div v-if="selectedChat.extractedFields[field.key]" class="caption">
                            {{ selectedChat.extractedFields[field.key].value }}
                          </div>
                          <div v-else class="caption grey--text italic">Not yet extracted</div>
                        </div>
                        <v-icon 
                          small 
                          :color="selectedChat.extractedFields[field.key] ? 'green' : 'grey lighten-1'"
                        >
                          {{ selectedChat.extractedFields[field.key] ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                        </v-icon>
                      </div>
                    </div>
                  </v-card>

                  <!-- Suggested Direction -->
                  <v-card outlined class="pa-3" color="blue lighten-5">
                    <div class="subtitle-2 mb-1">
                      <v-icon small color="blue" class="mr-1">mdi-lightbulb-outline</v-icon>
                      Suggested Direction
                    </div>
                    <div v-if="selectedChat.suggestedDirection" class="caption">
                      {{ selectedChat.suggestedDirection }}
                    </div>
                    <div v-else class="caption grey--text">
                      Waiting for conversation to begin...
                    </div>
                  </v-card>
                </template>

                <!-- No Chat Selected -->
                <v-card v-else outlined class="pa-4 flex-grow-1 d-flex flex-column align-center justify-center grey--text">
                  <v-icon large color="grey lighten-1" class="mb-2">mdi-clipboard-check-outline</v-icon>
                  <div class="caption text-center">Select a chat to see extraction results</div>
                </v-card>
              </div>
            </div>
          </v-sheet>
        </v-tab-item>

        <!-- Agents Tab -->
        <v-tab-item>
          <v-sheet class="pa-6" rounded color="grey lighten-4">
            <div class="d-flex flex-row">
              <!-- Left: Agent List -->
              <div class="d-flex flex-column mr-4" style="width: 280px; min-width: 280px;">
                <div class="d-flex flex-row align-center mb-3">
                  <div class="subtitle-1 font-weight-medium">Agents</div>
                  <v-spacer></v-spacer>
                  <v-btn icon small @click="openAgentDialog()">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </div>
                <v-card outlined>
                  <v-list dense v-if="agents.length > 0">
                    <v-list-item-group v-model="selectedAgentIndex" color="black">
                      <v-list-item 
                        v-for="(a, i) in agents" 
                        :key="a.id"
                        @click="selectAgent(i)"
                      >
                        <v-list-item-content>
                          <v-list-item-title>{{ a.name }}</v-list-item-title>
                          <v-list-item-subtitle class="caption">
                            {{ getTemplateName(a.templateId) || 'No template' }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                          <div class="d-flex flex-row">
                            <v-btn icon x-small @click.stop="openAgentDialog(i)">
                              <v-icon x-small>mdi-pencil</v-icon>
                            </v-btn>
                            <v-btn icon x-small color="red" @click.stop="removeAgent(i)">
                              <v-icon x-small>mdi-delete</v-icon>
                            </v-btn>
                          </div>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list-item-group>
                  </v-list>
                  <div v-else class="pa-4 text-center grey--text caption">
                    No agents yet
                  </div>
                </v-card>
              </div>

              <!-- Right: Selected Agent Config -->
              <div class="d-flex flex-column flex-grow-1">
                <template v-if="selectedAgent">
                  <!-- Agent Header -->
                  <div class="d-flex flex-row align-center mb-4">
                    <div class="d-flex flex-column">
                      <div class="subtitle-1 font-weight-medium">{{ selectedAgent.name }}</div>
                      <div class="caption grey--text">Configure agent personality and behavior</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-btn color="black" dark class="elevation-0" @click="saveAgentConfig">
                      <v-icon left>mdi-content-save</v-icon> Save
                    </v-btn>
                  </div>

                  <!-- Agent Configuration -->
                  <v-card outlined class="pa-4">
                    <div class="d-flex flex-column">
                      <!-- Template Selection -->
                      <div class="d-flex flex-row align-center mb-4">
                        <v-select
                          v-model="selectedAgent.templateId"
                          :items="templateOptions"
                          item-text="name"
                          item-value="id"
                          label="Template"
                          solo
                          outlined
                          dense
                          hide-details
                          style="max-width: 320px;"
                        >
                          <template v-slot:item="{ item }">
                            <div class="d-flex flex-column">
                              <div>{{ item.name }}</div>
                              <div class="caption grey--text">{{ item.fields.length }} fields</div>
                            </div>
                          </template>
                        </v-select>
                        <v-chip v-if="selectedAgent.templateId" small class="ml-3" color="blue" dark>
                          {{ getTemplateFieldCount(selectedAgent.templateId) }} fields
                        </v-chip>
                      </div>

                      <!-- Model Selection -->
                      <div class="subtitle-2 grey--text mb-2">Model</div>
                      <div class="d-flex flex-row mb-4">
                        <v-select
                          v-model="selectedAgent.provider"
                          :items="providers"
                          label="Provider"
                          solo
                          outlined
                          dense
                          hide-details
                          class="mr-2"
                          style="max-width: 160px;"
                        ></v-select>
                        <v-select
                          v-model="selectedAgent.tier"
                          :items="tiers"
                          label="Tier"
                          solo
                          outlined
                          dense
                          hide-details
                          style="max-width: 140px;"
                        ></v-select>
                      </div>

                      <!-- Personality -->
                      <div class="subtitle-2 grey--text mb-2">Personality</div>
                      <v-textarea
                        v-model="selectedAgent.personality"
                        solo
                        outlined
                        rows="3"
                        placeholder="Describe the agent's personality and tone. e.g. 'You are a friendly and enthusiastic assistant who loves helping kids create stories. Use simple language and lots of encouragement.'"
                        hide-details
                        class="mb-4"
                      ></v-textarea>

                      <!-- Purpose -->
                      <div class="subtitle-2 grey--text mb-2">Purpose</div>
                      <v-textarea
                        v-model="selectedAgent.purpose"
                        solo
                        outlined
                        rows="2"
                        placeholder="What is this agent's main goal? e.g. 'Help the user define all the elements needed to create a compelling story.'"
                        hide-details
                        class="mb-4"
                      ></v-textarea>

                      <!-- Restrictions -->
                      <div class="subtitle-2 grey--text mb-2">Restrictions</div>
                      <v-textarea
                        v-model="selectedAgent.restrictions"
                        solo
                        outlined
                        rows="2"
                        placeholder="What should the agent avoid? e.g. 'Keep content age-appropriate. Never suggest violent or scary themes.'"
                        hide-details
                        class="mb-4"
                      ></v-textarea>

                      <!-- Opening Message -->
                      <div class="subtitle-2 grey--text mb-2">Opening Message</div>
                      <v-textarea
                        v-model="selectedAgent.openingMessage"
                        solo
                        outlined
                        rows="2"
                        placeholder="The first message the agent sends to start the conversation. e.g. 'Hi there! I'm so excited to help you create a story today! What kind of adventure should we go on?'"
                        hide-details
                      ></v-textarea>
                    </div>
                  </v-card>

                  <!-- Linked Template Preview -->
                  <div v-if="selectedAgent.templateId && getTemplate(selectedAgent.templateId)" class="mt-4">
                    <div class="subtitle-2 grey--text mb-2">Template Fields Preview</div>
                    <v-card outlined>
                      <v-simple-table dense>
                        <thead>
                          <tr>
                            <th class="text-left">Field</th>
                            <th class="text-left">Type</th>
                            <th class="text-left">Required</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="field in getTemplate(selectedAgent.templateId).fields" :key="field.key">
                            <td class="py-2">{{ field.label }}</td>
                            <td class="py-2">
                              <v-chip x-small :color="fieldTypeColor(field.type)">{{ field.type }}</v-chip>
                            </td>
                            <td class="py-2">
                              <v-icon x-small :color="field.required ? 'green' : 'grey'">
                                {{ field.required ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                              </v-icon>
                            </td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </v-card>
                  </div>
                </template>

                <!-- No Agent Selected -->
                <div v-else class="d-flex flex-column align-center justify-center pa-12 grey--text">
                  <v-icon x-large color="grey lighten-1" class="mb-3">mdi-robot-outline</v-icon>
                  <div class="subtitle-1">No agent selected</div>
                  <div class="caption">Create or select an agent from the list</div>
                </div>
              </div>
            </div>
          </v-sheet>
        </v-tab-item>

        <!-- Templates Tab -->
        <v-tab-item>
          <v-sheet class="pa-6" rounded color="grey lighten-4">
            <div class="d-flex flex-row">
              <!-- Left: Template List -->
              <div class="d-flex flex-column mr-4" style="width: 280px; min-width: 280px;">
                <div class="d-flex flex-row align-center mb-3">
                  <div class="subtitle-1 font-weight-medium">Templates</div>
                  <v-spacer></v-spacer>
                  <v-btn icon small @click="openTemplateDialog()">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </div>
                <v-card outlined>
                  <v-list dense v-if="templates.length > 0">
                    <v-list-item-group v-model="selectedTemplateIndex" color="black">
                      <v-list-item 
                        v-for="(t, i) in templates" 
                        :key="t.id"
                        @click="selectTemplate(i)"
                      >
                        <v-list-item-content>
                          <v-list-item-title>{{ t.name }}</v-list-item-title>
                          <v-list-item-subtitle class="caption">
                            {{ t.fields.length }} fields · {{ t.fields.filter(f => f.required).length }} required
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                          <div class="d-flex flex-row">
                            <v-btn icon x-small @click.stop="openTemplateDialog(i)">
                              <v-icon x-small>mdi-pencil</v-icon>
                            </v-btn>
                            <v-btn icon x-small color="red" @click.stop="removeTemplate(i)">
                              <v-icon x-small>mdi-delete</v-icon>
                            </v-btn>
                          </div>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list-item-group>
                  </v-list>
                  <div v-else class="pa-4 text-center grey--text caption">
                    No templates yet
                  </div>
                </v-card>
              </div>

              <!-- Right: Selected Template Fields -->
              <div class="d-flex flex-column flex-grow-1">
                <template v-if="selectedTemplate">
                  <!-- Template Header -->
                  <div class="d-flex flex-row align-center mb-3">
                    <div class="d-flex flex-column">
                      <div class="subtitle-1 font-weight-medium">{{ selectedTemplate.name }}</div>
                      <div class="caption grey--text" v-if="selectedTemplate.description">{{ selectedTemplate.description }}</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-btn color="black" dark class="elevation-0" @click="openFieldDialog()">
                      <v-icon left>mdi-plus</v-icon> Add Field
                    </v-btn>
                  </div>

                  <!-- Fields List -->
                  <v-card outlined>
                    <v-simple-table v-if="selectedTemplate.fields.length > 0">
                      <thead>
                        <tr>
                          <th class="text-left" style="width: 140px">Key</th>
                          <th class="text-left" style="width: 160px">Label</th>
                          <th class="text-left" style="width: 100px">Type</th>
                          <th class="text-left" style="width: 80px">Required</th>
                          <th class="text-left">Extraction Hint</th>
                          <th class="text-left" style="width: 100px">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(field, index) in selectedTemplate.fields" :key="field.key">
                          <td class="py-3"><code>{{ field.key }}</code></td>
                          <td class="py-3">{{ field.label }}</td>
                          <td class="py-3">
                            <v-chip x-small :color="fieldTypeColor(field.type)">{{ field.type }}</v-chip>
                          </td>
                          <td class="py-3">
                            <v-icon small :color="field.required ? 'green' : 'grey'">
                              {{ field.required ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                            </v-icon>
                          </td>
                          <td class="py-3 caption" style="max-width: 240px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                            {{ field.extractionHint || '—' }}
                          </td>
                          <td class="py-3">
                            <v-btn icon small @click="openFieldDialog(index)">
                              <v-icon small>mdi-pencil</v-icon>
                            </v-btn>
                            <v-btn icon small color="red" @click="removeField(index)">
                              <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                          </td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                    <div v-else class="pa-6 text-center grey--text">
                      <v-icon large color="grey lighten-1" class="mb-2">mdi-format-list-checks</v-icon>
                      <div>No fields defined yet. Click "Add Field" to get started.</div>
                    </div>
                  </v-card>

                  <!-- Field Stats -->
                  <div class="d-flex flex-row mt-4" v-if="selectedTemplate.fields.length > 0">
                    <v-chip small outlined class="mr-2">
                      <v-icon left small>mdi-asterisk</v-icon>
                      {{ selectedTemplate.fields.filter(f => f.required).length }} required
                    </v-chip>
                    <v-chip small outlined>
                      <v-icon left small>mdi-circle-outline</v-icon>
                      {{ selectedTemplate.fields.filter(f => !f.required).length }} optional
                    </v-chip>
                  </div>
                </template>

                <!-- No Template Selected -->
                <div v-else class="d-flex flex-column align-center justify-center pa-12 grey--text">
                  <v-icon x-large color="grey lighten-1" class="mb-3">mdi-file-document-outline</v-icon>
                  <div class="subtitle-1">No template selected</div>
                  <div class="caption">Create or select a template from the list</div>
                </div>
              </div>
            </div>
          </v-sheet>
        </v-tab-item>
      </v-tabs-items>

      <!-- Add/Edit Agent Dialog -->
      <v-dialog v-model="agentDialog" max-width="480">
        <v-card>
          <v-card-title>{{ editingAgentIndex === null ? 'New Agent' : 'Edit Agent' }}</v-card-title>
          <v-card-text>
            <div class="d-flex flex-column">
              <v-text-field 
                v-model="agentForm.name" 
                label="Agent Name" 
                solo 
                outlined 
                dense
                hint="e.g. Story Sparky, Fitness Coach"
                persistent-hint
              ></v-text-field>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="agentDialog = false">Cancel</v-btn>
            <v-btn color="black" dark class="elevation-0" @click="saveAgent">
              {{ editingAgentIndex === null ? 'Create' : 'Save' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Add/Edit Template Dialog -->
      <v-dialog v-model="templateDialog" max-width="480">
        <v-card>
          <v-card-title>{{ editingTemplateIndex === null ? 'New Template' : 'Edit Template' }}</v-card-title>
          <v-card-text>
            <div class="d-flex flex-column">
              <v-text-field 
                v-model="templateForm.name" 
                label="Template Name" 
                solo 
                outlined 
                dense
                hint="e.g. Story Elements, Workout Onboarding"
                persistent-hint
              ></v-text-field>
              <v-textarea 
                v-model="templateForm.description" 
                label="Description (optional)" 
                solo 
                outlined 
                rows="2"
                class="mt-3"
              ></v-textarea>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="templateDialog = false">Cancel</v-btn>
            <v-btn color="black" dark class="elevation-0" @click="saveTemplate">
              {{ editingTemplateIndex === null ? 'Create' : 'Save' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Add/Edit Field Dialog -->
      <v-dialog v-model="fieldDialog" max-width="560">
        <v-card>
          <v-card-title>{{ editingFieldIndex === null ? 'Add Field' : 'Edit Field' }}</v-card-title>
          <v-card-text>
            <div class="d-flex flex-column">
              <div class="d-flex flex-row">
                <v-text-field 
                  v-model="fieldForm.key" 
                  label="Key" 
                  solo 
                  outlined 
                  dense
                  class="mr-2"
                  hint="Unique identifier (e.g. hero, setting)"
                  persistent-hint
                ></v-text-field>
                <v-text-field 
                  v-model="fieldForm.label" 
                  label="Label" 
                  solo 
                  outlined 
                  dense
                  hint="Display name (e.g. Main Character)"
                  persistent-hint
                ></v-text-field>
              </div>
              <div class="d-flex flex-row mt-2">
                <v-select 
                  v-model="fieldForm.type" 
                  :items="fieldTypes" 
                  label="Type" 
                  solo 
                  outlined 
                  dense
                  class="mr-2"
                ></v-select>
                <v-checkbox 
                  v-model="fieldForm.required" 
                  label="Required" 
                  dense
                  class="mt-1"
                ></v-checkbox>
              </div>
              <v-textarea 
                v-model="fieldForm.extractionHint" 
                label="Extraction Hint" 
                solo 
                outlined 
                rows="2"
                hint="Help the evaluator understand what to look for"
                persistent-hint
                class="mt-2"
              ></v-textarea>
              <v-textarea 
                v-if="fieldForm.type === 'enum'"
                v-model="fieldForm.optionsText" 
                label="Options (one per line)" 
                solo 
                outlined 
                rows="3"
                hint="For enum type: list valid options"
                persistent-hint
                class="mt-2"
              ></v-textarea>
              <v-checkbox 
                v-if="!fieldForm.required"
                v-model="fieldForm.skipIfNotMentioned" 
                label="Skip if not mentioned (don't actively ask)" 
                dense
                class="mt-2"
              ></v-checkbox>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="fieldDialog = false">Cancel</v-btn>
            <v-btn color="black" dark class="elevation-0" @click="saveField">
              {{ editingFieldIndex === null ? 'Add' : 'Save' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </AdminLayout>
</template>

<script>
import { doc, setDoc, getDoc, collection, getDocs, deleteDoc } from 'firebase/firestore'
import { db } from '@/firebase/init'
import AdminLayout from '@/layouts/Admin.vue'

export default {
  name: 'AdminCocoro',
  components: { AdminLayout },
  data() {
    return {
      progressValue: 100,
      tab: 0,
      providers: ['openai', 'gemini', 'claude', 'grok'],
      tiers: ['best', 'fast', 'mini'],
      loading: false,

      // Agents
      agents: [],
      selectedAgentIndex: null,

      // Agent Dialog
      agentDialog: false,
      editingAgentIndex: null,
      agentForm: {
        name: ''
      },

      // Templates
      templates: [],
      selectedTemplateIndex: null,

      // Template Dialog
      templateDialog: false,
      editingTemplateIndex: null,
      templateForm: {
        name: '',
        description: ''
      },

      // Field Dialog
      fieldDialog: false,
      editingFieldIndex: null,
      fieldForm: {
        key: '',
        label: '',
        type: 'text',
        required: true,
        extractionHint: '',
        options: [],
        optionsText: '',
        skipIfNotMentioned: false
      },
      fieldTypes: ['text', 'number', 'enum', 'boolean', 'date'],

      // Chat State
      chats: [],
      selectedChatIndex: null,
      chatAgentId: null,
      chatInput: '',
      chatLoading: false
    }
  },
  computed: {
    isAdmin() {
      const email = (this.$root && this.$root.user && this.$root.user.email) || ''
      return email === 'aaronfarr@gmail.com' || email === 'socratesgx@gmail.com' || email === 'aaron@farrindustries.com'
    },
    selectedAgent() {
      if (this.selectedAgentIndex === null || this.selectedAgentIndex === undefined) return null
      return this.agents[this.selectedAgentIndex] || null
    },
    selectedTemplate() {
      if (this.selectedTemplateIndex === null || this.selectedTemplateIndex === undefined) return null
      return this.templates[this.selectedTemplateIndex] || null
    },
    templateOptions() {
      return [{ id: null, name: 'No template', fields: [] }, ...this.templates]
    },
    selectedChat() {
      if (this.selectedChatIndex === null || this.selectedChatIndex === undefined) return null
      return this.chats[this.selectedChatIndex] || null
    },
    chatTemplateFields() {
      if (!this.selectedChat) return []
      const agent = this.agents.find(a => a.id === this.selectedChat.agentId)
      if (!agent || !agent.templateId) return []
      const template = this.templates.find(t => t.id === agent.templateId)
      return template ? template.fields : []
    },
    extractedFieldCount() {
      if (!this.selectedChat) return 0
      return Object.keys(this.selectedChat.extractedFields || {}).length
    },
    totalFieldCount() {
      return this.chatTemplateFields.length
    },
    extractionProgress() {
      if (this.totalFieldCount === 0) return 0
      return (this.extractedFieldCount / this.totalFieldCount) * 100
    }
  },
  methods: {
    // Date formatter
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    },

    // Chat helpers
    getChatAgentName(agentId) {
      const agent = this.agents.find(a => a.id === agentId)
      return agent ? agent.name : 'Unknown Agent'
    },
    getChatTemplateName(agentId) {
      const agent = this.agents.find(a => a.id === agentId)
      if (!agent || !agent.templateId) return 'No template'
      const template = this.templates.find(t => t.id === agent.templateId)
      return template ? template.name : 'No template'
    },

    // Chat methods
    selectChat(index) {
      this.selectedChatIndex = index
    },
    startNewChat() {
      if (!this.chatAgentId) return
      
      const agent = this.agents.find(a => a.id === this.chatAgentId)
      const newChat = {
        id: this.generateId(),
        agentId: this.chatAgentId,
        messages: [],
        extractedFields: {},
        suggestedDirection: '',
        complete: false,
        createdAt: new Date().toISOString()
      }

      // Add opening message if agent has one
      if (agent && agent.openingMessage) {
        newChat.messages.push({
          role: 'assistant',
          content: agent.openingMessage
        })
      }

      this.chats.unshift(newChat)
      this.selectedChatIndex = 0
      this.$snackbar?.success('New chat started')
    },
    async sendMessage() {
      if (!this.chatInput.trim() || !this.selectedChat) return

      const userMessage = this.chatInput.trim()
      this.chatInput = ''
      this.chatLoading = true

      // Add user message immediately
      this.selectedChat.messages.push({
        role: 'user',
        content: userMessage
      })

      try {
        // Get the agent and template for this chat
        const agent = this.agents.find(a => a.id === this.selectedChat.agentId)
        if (!agent) throw new Error('Agent not found')

        const template = agent.templateId 
          ? this.templates.find(t => t.id === agent.templateId) 
          : null

        // Build conversation history (excluding the message we just added)
        const conversation = this.selectedChat.messages.slice(0, -1)

        // Call the Cocoro backend
        const token = await this.$root.user.getIdToken()
        const response = await fetch(this.$endpoints.ideaverseInvoke, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            agent: 'cocoro',
            action: 'chat',
            prompt: userMessage,
            conversation,
            agentConfig: {
              name: agent.name,
              personality: agent.personality,
              purpose: agent.purpose,
              restrictions: agent.restrictions
            },
            template: template ? {
              name: template.name,
              fields: template.fields
            } : null,
            provider: agent.provider || 'gemini',
            tier: agent.tier || 'fast'
          })
        })

        const data = await response.json()
        if (!response.ok) throw new Error(data.error || 'Failed to get response')

        // Add assistant response
        this.selectedChat.messages.push({
          role: 'assistant',
          content: data.response
        })

        // Update evaluation data
        if (data.evaluation) {
          // Merge new extracted fields with existing
          this.selectedChat.extractedFields = {
            ...this.selectedChat.extractedFields,
            ...data.evaluation.extractedFields
          }
          this.selectedChat.suggestedDirection = data.evaluation.suggestedDirection || ''
          this.selectedChat.complete = data.evaluation.complete || false
        }

      } catch (error) {
        console.error('Chat error:', error)
        this.$snackbar?.error(error.message || 'Failed to send message')
        // Remove the user message if we failed
        this.selectedChat.messages.pop()
      } finally {
        this.chatLoading = false
      }
    },
    clearChat() {
      if (!this.selectedChat) return
      if (confirm('Are you sure you want to delete this chat?')) {
        this.chats.splice(this.selectedChatIndex, 1)
        this.selectedChatIndex = this.chats.length > 0 ? 0 : null
        this.$snackbar?.success('Chat deleted')
      }
    },

    // Field type color helper
    fieldTypeColor(type) {
      const colors = {
        text: 'blue',
        number: 'purple',
        enum: 'orange',
        boolean: 'teal',
        date: 'pink'
      }
      return colors[type] || 'grey'
    },

    // Generate unique ID
    generateId() {
      return Date.now().toString(36) + Math.random().toString(36).substr(2, 9)
    },

    // Template helpers
    getTemplate(templateId) {
      return this.templates.find(t => t.id === templateId) || null
    },
    getTemplateName(templateId) {
      const t = this.getTemplate(templateId)
      return t ? t.name : null
    },
    getTemplateFieldCount(templateId) {
      const t = this.getTemplate(templateId)
      return t ? t.fields.length : 0
    },

    // Agent methods
    selectAgent(index) {
      this.selectedAgentIndex = index
    },

    openAgentDialog(index = null) {
      this.editingAgentIndex = index
      if (index !== null) {
        const agent = this.agents[index]
        this.agentForm = {
          name: agent.name
        }
      } else {
        this.agentForm = {
          name: ''
        }
      }
      this.agentDialog = true
    },

    async saveAgent() {
      if (!this.agentForm.name.trim()) {
        this.$snackbar?.error('Agent name is required')
        return
      }

      try {
        if (this.editingAgentIndex !== null) {
          // Update existing name only
          const agent = this.agents[this.editingAgentIndex]
          agent.name = this.agentForm.name
          agent.updatedAt = new Date().toISOString()
          await setDoc(doc(db, 'cocoro_agents', agent.id), agent)
          this.$snackbar?.success('Agent updated')
        } else {
          // Create new
          const newAgent = {
            id: this.generateId(),
            name: this.agentForm.name,
            templateId: null,
            provider: 'gemini',
            tier: 'fast',
            personality: '',
            purpose: '',
            restrictions: '',
            openingMessage: '',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }
          await setDoc(doc(db, 'cocoro_agents', newAgent.id), newAgent)
          this.agents.push(newAgent)
          this.selectedAgentIndex = this.agents.length - 1
          this.$snackbar?.success('Agent created')
        }
        this.agentDialog = false
      } catch (e) {
        console.error('Save agent error:', e)
        this.$snackbar?.error('Failed to save agent: ' + e.message)
      }
    },

    async saveAgentConfig() {
      if (!this.selectedAgent) return
      try {
        this.selectedAgent.updatedAt = new Date().toISOString()
        await setDoc(doc(db, 'cocoro_agents', this.selectedAgent.id), this.selectedAgent)
        this.$snackbar?.success('Agent configuration saved')
      } catch (e) {
        console.error('Save agent config error:', e)
        this.$snackbar?.error('Failed to save agent: ' + e.message)
      }
    },

    async removeAgent(index) {
      const agent = this.agents[index]
      if (!confirm(`Delete agent "${agent.name}"?`)) return

      try {
        await deleteDoc(doc(db, 'cocoro_agents', agent.id))
        this.agents.splice(index, 1)
        if (this.selectedAgentIndex === index) {
          this.selectedAgentIndex = this.agents.length > 0 ? 0 : null
        } else if (this.selectedAgentIndex > index) {
          this.selectedAgentIndex--
        }
        this.$snackbar?.success('Agent removed')
      } catch (e) {
        console.error('Remove agent error:', e)
        this.$snackbar?.error('Failed to remove agent: ' + e.message)
      }
    },

    // Template methods
    selectTemplate(index) {
      this.selectedTemplateIndex = index
    },

    openTemplateDialog(index = null) {
      this.editingTemplateIndex = index
      if (index !== null) {
        const template = this.templates[index]
        this.templateForm = {
          name: template.name,
          description: template.description || ''
        }
      } else {
        this.templateForm = {
          name: '',
          description: ''
        }
      }
      this.templateDialog = true
    },

    async saveTemplate() {
      if (!this.templateForm.name.trim()) {
        this.$snackbar?.error('Template name is required')
        return
      }

      try {
        if (this.editingTemplateIndex !== null) {
          // Update existing
          const template = this.templates[this.editingTemplateIndex]
          template.name = this.templateForm.name
          template.description = this.templateForm.description
          template.updatedAt = new Date().toISOString()
          await setDoc(doc(db, 'cocoro_templates', template.id), template)
          this.$snackbar?.success('Template updated')
        } else {
          // Create new
          const newTemplate = {
            id: this.generateId(),
            name: this.templateForm.name,
            description: this.templateForm.description,
            fields: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }
          await setDoc(doc(db, 'cocoro_templates', newTemplate.id), newTemplate)
          this.templates.push(newTemplate)
          this.selectedTemplateIndex = this.templates.length - 1
          this.$snackbar?.success('Template created')
        }
        this.templateDialog = false
      } catch (e) {
        console.error('Save template error:', e)
        this.$snackbar?.error('Failed to save template: ' + e.message)
      }
    },

    async removeTemplate(index) {
      const template = this.templates[index]
      if (!confirm(`Delete template "${template.name}"?`)) return

      try {
        await deleteDoc(doc(db, 'cocoro_templates', template.id))
        this.templates.splice(index, 1)
        if (this.selectedTemplateIndex === index) {
          this.selectedTemplateIndex = this.templates.length > 0 ? 0 : null
        } else if (this.selectedTemplateIndex > index) {
          this.selectedTemplateIndex--
        }
        this.$snackbar?.success('Template removed')
      } catch (e) {
        console.error('Remove template error:', e)
        this.$snackbar?.error('Failed to remove template: ' + e.message)
      }
    },

    // Field methods
    openFieldDialog(index = null) {
      if (!this.selectedTemplate) return

      this.editingFieldIndex = index
      if (index !== null) {
        const field = this.selectedTemplate.fields[index]
        this.fieldForm = {
          key: field.key,
          label: field.label,
          type: field.type,
          required: field.required,
          extractionHint: field.extractionHint || '',
          options: field.options || [],
          optionsText: (field.options || []).join('\n'),
          skipIfNotMentioned: field.skipIfNotMentioned || false
        }
      } else {
        this.fieldForm = {
          key: '',
          label: '',
          type: 'text',
          required: true,
          extractionHint: '',
          options: [],
          optionsText: '',
          skipIfNotMentioned: false
        }
      }
      this.fieldDialog = true
    },

    async saveField() {
      if (!this.selectedTemplate) return

      const field = {
        key: this.fieldForm.key.toLowerCase().replace(/\s+/g, '_'),
        label: this.fieldForm.label,
        type: this.fieldForm.type,
        required: this.fieldForm.required,
        extractionHint: this.fieldForm.extractionHint
      }

      // Parse options for enum type
      if (this.fieldForm.type === 'enum') {
        field.options = this.fieldForm.optionsText
          .split('\n')
          .map(o => o.trim())
          .filter(o => o.length > 0)
      }

      // Add skipIfNotMentioned for optional fields
      if (!field.required) {
        field.skipIfNotMentioned = this.fieldForm.skipIfNotMentioned
      }

      if (this.editingFieldIndex !== null) {
        this.selectedTemplate.fields.splice(this.editingFieldIndex, 1, field)
      } else {
        this.selectedTemplate.fields.push(field)
      }

      // Save template to Firestore
      try {
        this.selectedTemplate.updatedAt = new Date().toISOString()
        await setDoc(doc(db, 'cocoro_templates', this.selectedTemplate.id), this.selectedTemplate)
        this.fieldDialog = false
        this.$snackbar?.success(this.editingFieldIndex !== null ? 'Field updated' : 'Field added')
      } catch (e) {
        console.error('Save field error:', e)
        this.$snackbar?.error('Failed to save field: ' + e.message)
      }
    },

    async removeField(index) {
      if (!this.selectedTemplate) return
      this.selectedTemplate.fields.splice(index, 1)
      
      // Save template to Firestore
      try {
        this.selectedTemplate.updatedAt = new Date().toISOString()
        await setDoc(doc(db, 'cocoro_templates', this.selectedTemplate.id), this.selectedTemplate)
        this.$snackbar?.success('Field removed')
      } catch (e) {
        console.error('Remove field error:', e)
        this.$snackbar?.error('Failed to remove field: ' + e.message)
      }
    },

    // Load methods
    async loadTemplates() {
      try {
        const snapshot = await getDocs(collection(db, 'cocoro_templates'))
        this.templates = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        if (this.templates.length > 0 && this.selectedTemplateIndex === null) {
          this.selectedTemplateIndex = 0
        }
      } catch (e) {
        console.error('Load templates error:', e)
        this.$snackbar?.error('Failed to load templates')
      }
    },

    async loadAgents() {
      try {
        const snapshot = await getDocs(collection(db, 'cocoro_agents'))
        this.agents = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        if (this.agents.length > 0 && this.selectedAgentIndex === null) {
          this.selectedAgentIndex = 0
        }
      } catch (e) {
        console.error('Load agents error:', e)
        this.$snackbar?.error('Failed to load agents')
      }
    },

    async loadData() {
      this.loading = true
      this.progressValue = 0
      try {
        await Promise.all([
          this.loadTemplates(),
          this.loadAgents()
        ])
      } finally {
        this.loading = false
        this.progressValue = 100
      }
    }
  },
  mounted() {
    // Wait for user to be available, then load data
    const runLoad = () => {
      if (this.$root && this.$root.user) {
        this.loadData()
      }
    }
    
    if (this.$root && this.$root.user) {
      runLoad()
    } else {
      const timer = setInterval(() => {
        if (this.$root && this.$root.user) {
          clearInterval(timer)
          runLoad()
        }
      }, 300)
      setTimeout(() => clearInterval(timer), 10000)
    }
  }
}
</script>
